/**
 * Shared postMessage bridge utility for communicating with the playEngine iframe.
 *
 * Used by containerPlayEngineVersionSettings and containerPlayEngineGeneralSettings.
 */

/**
 * Send a command to the playEngine iframe and wait for the matching response.
 * The bridge matches on `requestId` so parallel commands work safely.
 *
 * @param {string} iFrameId   - DOM id of the playEngine iframe element
 * @param {object} payload    - message payload; `type` is required
 * @param {number} [timeout]  - ms before rejecting (default 5000)
 * @returns {Promise<object>} - resolves with the full response message
 */
function generateId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function sendCommand(iFrameId, payload, timeout = 5000) {
  return new Promise((resolve, reject) => {
    const iframe = document.getElementById(iFrameId);
    if (!iframe || !iframe.contentWindow) {
      reject(new Error("PlayEngine iframe not found"));
      return;
    }

    const requestId = generateId();
    const timer = setTimeout(() => {
      window.removeEventListener("message", handler);
      reject(new Error(`playEngine command "${payload.type}" timed out`));
    }, timeout);

    function handler(event) {
      if (event.source !== iframe.contentWindow) return;
      if (event.data?.requestId !== requestId) return;
      clearTimeout(timer);
      window.removeEventListener("message", handler);
      resolve(event.data);
    }

    window.addEventListener("message", handler);
    iframe.contentWindow.postMessage({ ...payload, requestId }, "*");
  });
}
